import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Divider,
  Paper,
  Chip,
  Grid,
  CircularProgress,
  Alert
} from '@mui/material';
import RouterIcon from '@mui/icons-material/Router';
import { DashboardService } from '../../../service/dashboard.service';
import { PiService } from '../../../service/piService';
// ENTFERNT: import { SettingsDeploymentService } from '../../../service/settings-deployment.service';

interface RaspberryPi {
  id: string;
  name: string;
  status: string;
  ipAddress: string;
  serialNumber: string;
}

const RouterSettingsView: React.FC = () => {
  const [raspberryPis, setRaspberryPis] = useState<RaspberryPi[]>([]);
  const [accountId, setAccountId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editedPi, setEditedPi] = useState<RaspberryPi | null>(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);

  useEffect(() => {
    fetchRouterData();
  }, []);

  const fetchRouterData = async () => {
    try {
      setLoading(true);
      const data = await DashboardService.getDashboard();
      
      if (data.id) {
        setAccountId(data.id);
        await fetchRaspberryPi(data.id);
      }
    } catch (error) {
      console.error('Fehler beim Abrufen der Router-Daten:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRaspberryPi = async (accountToUse: string) => {
    try {
      if (!accountToUse) {
        setRaspberryPis([]);
        return;
      }
      
      const response = await PiService.getPiForOwner(accountToUse);
      
      if (!Array.isArray(response)) {
        setRaspberryPis([]);
        return;
      }
      
      // Konvertiere das Backend-Format in das Dashboard-Format
      const dashboardPis: RaspberryPi[] = response.map((pi: any) => ({
        id: pi._id,
        name: pi.name,
        status: pi.status,
        ipAddress: pi.ipAddress || '',
        serialNumber: pi.serialNumber
      }));
      
      setRaspberryPis(dashboardPis);
      if (dashboardPis.length > 0) {
        setEditedPi({...dashboardPis[0]});
      }
    } catch (error) {
      console.error('Fehler beim Laden der Raspberry Pis:', error);
      setRaspberryPis([]);
    }
  };

  const handleEdit = () => {
    setEditMode(true);
    setSaveError(null);
    setSaveSuccess(false);
    if (raspberryPis.length > 0) {
      setEditedPi({...raspberryPis[0]});
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setSaveError(null);
    setSaveSuccess(false);
    setEditedPi(raspberryPis.length > 0 ? {...raspberryPis[0]} : null);
  };

  const handleInputChange = (field: keyof RaspberryPi, value: string) => {
    if (editedPi) {
      setEditedPi({
        ...editedPi,
        [field]: value
      });
    }
  };

  const handleSave = async () => {
    if (!editedPi) return;
    
    // IP-Adresse validieren
    if (editedPi.ipAddress && !/^(\d{1,3}\.){3}\d{1,3}$/.test(editedPi.ipAddress)) {
      setSaveError('Bitte geben Sie eine gültige IP-Adresse ein (z.B. 192.168.1.100)');
      return;
    }
    
    try {
      setSaveLoading(true);
      setSaveError(null);
      setSaveSuccess(false);

      // Nur die ipAddress an das Backend senden
      const updatedPi = await PiService.updatePi(editedPi.id, {
        ipAddress: editedPi.ipAddress
      });
      
      console.log('Router IP-Adresse erfolgreich aktualisiert:', updatedPi);
      
      // Update local state to reflect changes
      setRaspberryPis([{
        ...editedPi,
        ipAddress: updatedPi.ipAddress || ''
      }]);
      
      setEditMode(false);
      setSaveSuccess(true);
      
      // Löse Deployment aus
      // ENTFERNT: await SettingsDeploymentService.triggerDeploymentAfterSettingsChange('Router-Einstellungen');
      
      // Hide success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Fehler beim Speichern der Router-Daten:', error);
      setSaveError('Die Router-IP konnte nicht gespeichert werden. Bitte versuchen Sie es später erneut.');
    } finally {
      setSaveLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '100%' }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <RouterIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h5" component="h1">
          Router
        </Typography>
      </Box>

      <Card sx={{ mb: 4, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Router-Einstellungen</Typography>
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          {raspberryPis.length > 0 ? (
            editMode ? (
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label="IP-Adresse"
                  value={editedPi?.ipAddress || ''}
                  onChange={(e) => handleInputChange('ipAddress', e.target.value)}
                  margin="normal"
                  placeholder="z.B. 192.168.1.100"
                  helperText="IP-Adresse des Routers"
                  error={!!saveError}
                />
                
                {saveError && (
                  <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                    {saveError}
                  </Alert>
                )}
                
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                  <Button 
                    variant="outlined" 
                    onClick={handleCancel}
                    disabled={saveLoading}
                  >
                    Abbrechen
                  </Button>
                  <Button 
                    variant="contained" 
                    onClick={handleSave}
                    disabled={saveLoading}
                  >
                    {saveLoading ? <CircularProgress size={24} /> : 'Speichern'}
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">IP-Adresse</Typography>
                        <Typography variant="body1">{raspberryPis[0]?.ipAddress || 'Nicht verfügbar'}</Typography>
                      </Box>
                      <Button 
                        variant="outlined" 
                        onClick={handleEdit}
                        sx={{ 
                          color: 'black', 
                          borderColor: 'black',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            borderColor: 'black'
                          }
                        }}
                      >
                        Bearbeiten
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
                
                {saveSuccess && (
                  <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
                    Router-IP wurde erfolgreich gespeichert
                  </Alert>
                )}
              </Box>
            )
          ) : (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Kein Router konfiguriert
              </Alert>
              <Button variant="contained">
                Router hinzufügen
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default RouterSettingsView; 